/**
 * The post-create confirmation's three groups, in their own file so they
 * can be MOUNTED. `CreateTenantPage.jsx` imports `react-router-dom`, which
 * this repo's Jest cannot resolve, so nothing inside it is render-testable;
 * the section that most needed looking at — the one that rendered
 * `success.environment` as "—" for every tenant ever created — was the one
 * no test could see.
 *
 * THREE GROUPS, VISUALLY SEPARATE, NEVER MERGED:
 *
 *   ENVIRONMENT                 what was stamped on the tenant
 *   INCLUDED PLATFORM SERVICES  managed storage and email — every tenant,
 *                               every environment, not connections
 *   DEMO CONNECTIONS            what the operator asked for, or why they
 *                               could not
 *
 * Each is a bordered surface with its own eyebrow, because the failure mode
 * is a reader carrying a fact across a boundary — "storage is included"
 * becoming "a storage connection is attached", or "demo connections are on"
 * becoming "WhatsApp is enabled". The note under the third group says the
 * second of those in words.
 */
import React from 'react';
import { Globe, Package, PlugZap } from 'lucide-react';

import StatusBadge from '../../../components/admin/StatusBadge';
import { describeEnvironment } from './environmentPresentation';
import ManagedServicesChips from './ManagedServicesChips';
import {
  DEMO_CONNECTIONS_FEATURE_NOTE, MANAGED_SERVICES_NOTE,
  describeCreatedDemoConnections, describeManagedServices,
} from './managedServices';

/** One group: an eyebrow with a flat glyph, then its content. */
const Group = ({ icon: Glyph, eyebrow, testId, children }) => (
  <section className="rounded-xl border border-border bg-muted/20 p-4" data-testid={testId}>
    <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
      <Glyph className="h-4 w-4 shrink-0" aria-hidden="true" />
      {eyebrow}
    </p>
    <div className="mt-2">{children}</div>
  </section>
);

export default function CreatedTenantSummary({ success }) {
  const env = describeEnvironment(success?.environment);
  const services = describeManagedServices(success?.managed_services);
  const demo = describeCreatedDemoConnections(success);

  return (
    <div className="space-y-3" data-testid="created-tenant-summary">
      <Group icon={Globe} eyebrow="Environment" testId="success-environment-group">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium text-foreground" data-testid="success-environment-label">
            {env.label}
          </span>
          {env.code && <StatusBadge status={env.code.toLowerCase()} label={env.code} dot={false} />}
        </div>
      </Group>

      <Group icon={Package} eyebrow="Included platform services" testId="success-managed-services-group">
        <ManagedServicesChips services={services} testIdPrefix="success-managed-service" />
        <p className="mt-2 text-xs text-muted-foreground">{MANAGED_SERVICES_NOTE}</p>
      </Group>

      <Group icon={PlugZap} eyebrow="Demo connections" testId="success-demo-connections-group">
        <p
          className={`text-sm font-medium ${demo.state === 'on' ? 'text-foreground' : 'text-muted-foreground'}`}
          data-testid={`success-demo-connections-${demo.state}`}
        >
          {demo.state === 'on' ? (
            <>
              On
              {demo.profileId && (
                <span className="ml-2 font-mono text-xs font-normal text-muted-foreground break-all">
                  {demo.profileId}
                </span>
              )}
            </>
          ) : demo.label}
        </p>
        <p className="mt-2 text-xs text-muted-foreground" data-testid="success-demo-connections-note">
          {DEMO_CONNECTIONS_FEATURE_NOTE}
        </p>
      </Group>
    </div>
  );
}
